<template>
  <div class="gamelayout-wrapper">
    <div class="game-layout">
      <AppHeader />
      <main class="game-content">
        <router-view />
      </main>
      <AppFooter />

      <NicknameModal v-if="needsNickname" />
      <ChatWindow />
      
      <!-- Side Info Panel (Only appears on City / Hero tabs if enabled) -->
      <SideInfoPanel 
        v-if="isPanelVisible" 
        :type="panelType" 
        :data="panelData" 
      />

      <div
        v-if="authStore.user?.race"
        class="ambiance-control"
        :class="{
          'panel-active':
            isPanelVisible || uiStore.sidePanelOpen || route.name === 'GameBattle',
          'volume-open': volumeSliderOpen,
        }"
        @pointerenter="openVolumeSlider"
        @pointerleave="scheduleCloseVolumeSlider"
        @focusin="openVolumeSlider"
        @focusout="onVolumeSliderFocusOut"
      >
        <input
          type="range"
          class="ambiance-volume-slider"
          min="0"
          max="1"
          step="0.01"
          orient="vertical"
          :value="uiStore.userVolume"
          aria-label="Music volume"
          title="Music volume"
          @click.stop
          @pointerdown="onVolumeSliderPointerDown"
          @input="uiStore.setAmbianceVolume($event.target.value)"
        />
        <button
          type="button"
          @click="toggleRaceAmbiance"
          class="floating-audio-btn"
          :title="uiStore.isAmbiancePlaying ? 'Mute Music' : 'Play Music'"
        >
          <i :class="uiStore.isAmbiancePlaying ? 'fas fa-volume-up' : 'fas fa-volume-mute'"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, computed, ref } from "vue";
import { useRoute } from "vue-router";
import { useUiStore } from "../store/ui";
import { useAuthStore } from "../store/auth";
import { useGameStore } from "../store/game";
import AppHeader from "../components/AppHeader.vue";
import AppFooter from "../components/AppFooter.vue";
import NicknameModal from "../components/NicknameModal.vue";
import ChatWindow from "../components/ChatWindow.vue";
import SideInfoPanel from "../components/SideInfoPanel.vue";
import { getRaceAudioPath, toggleRaceAmbiance } from "../utils/ambiance";

const route = useRoute();
const uiStore = useUiStore();
const authStore = useAuthStore();
const gameStore = useGameStore();

const isPanelVisible = computed(() => {
  if (route.name === 'GameHero' && gameStore.isHeroPanelEnabled) return true;
  if (route.name === 'GameCity' && gameStore.isCityPanelEnabled) return true;
  if (route.name === 'GameQuest' && gameStore.isQuestPanelEnabled) return true;
  return false;
});

const panelType = computed(() => {
  if (route.name === 'GameHero') return 'hero';
  if (route.name === 'GameCity') return 'city';
  if (route.name === 'GameQuest') return 'quest';
  return null;
});

const panelData = computed(() => {
  if (panelType.value === 'hero') return gameStore.activeHero;
  if (panelType.value === 'city') return gameStore.activeCity;
  if (panelType.value === 'quest') return gameStore.activeHero;
  return null;
});

const needsNickname = computed(() => {
  const u = authStore.user;
  return u && u.race && (!u.nickname || !u.avatar_id);
});

// --- Audio Logic ---
const autoStartTimeout = ref(null);
const volumeSliderOpen = ref(false);
const volumeSliderDragging = ref(false);
let volumeSliderCloseTimer = null;

const openVolumeSlider = () => {
  if (volumeSliderCloseTimer) {
    clearTimeout(volumeSliderCloseTimer);
    volumeSliderCloseTimer = null;
  }
  volumeSliderOpen.value = true;
};

const scheduleCloseVolumeSlider = () => {
  if (volumeSliderDragging.value) return;
  if (volumeSliderCloseTimer) clearTimeout(volumeSliderCloseTimer);
  volumeSliderCloseTimer = setTimeout(() => {
    volumeSliderCloseTimer = null;
    if (!volumeSliderDragging.value) {
      volumeSliderOpen.value = false;
    }
  }, 280);
};

const onVolumeSliderPointerUp = () => {
  volumeSliderDragging.value = false;
  window.removeEventListener("pointerup", onVolumeSliderPointerUp);
  scheduleCloseVolumeSlider();
};

const onVolumeSliderPointerDown = () => {
  volumeSliderDragging.value = true;
  openVolumeSlider();
  window.addEventListener("pointerup", onVolumeSliderPointerUp);
};

const onVolumeSliderFocusOut = (event) => {
  if (event.currentTarget.contains(event.relatedTarget)) return;
  scheduleCloseVolumeSlider();
};

onMounted(() => {
  gameStore.fetchPlayerResources().then(() => {
    scheduleNextClaim();
  });

  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("focus", onWindowFocus);

  const isNewSession = sessionStorage.getItem("newSession");
  const coarsePointer =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches;
  const narrowViewport = window.innerWidth < 900;
  const skipSplash =
    needsNickname.value || coarsePointer || narrowViewport;

  if (isNewSession && !skipSplash) {
    uiStore.showSplashScreen();
  }
  if (isNewSession) {
    sessionStorage.removeItem("newSession");
  }

  // Auto-start ambiance music after 5 seconds if user has selected a race
  if (authStore.user?.race) {
    autoStartTimeout.value = setTimeout(() => {
      const audioSrc = getRaceAudioPath(authStore.user.race);
      if (audioSrc && !uiStore.isAmbiancePlaying) {
        uiStore.toggleAmbiance(audioSrc);
      }
    }, 5000);
  }
});

onUnmounted(() => {
  document.removeEventListener("visibilitychange", onVisibilityChange);
  window.removeEventListener("focus", onWindowFocus);
  window.removeEventListener("pointerup", onVolumeSliderPointerUp);
  stopResourceClaimTimer();

  if (volumeSliderCloseTimer) {
    clearTimeout(volumeSliderCloseTimer);
    volumeSliderCloseTimer = null;
  }

  // Cleanup audio
  if (autoStartTimeout.value) {
    clearTimeout(autoStartTimeout.value);
  }
  uiStore.stopAudio();
});

let claimTimerId = null;

const stopResourceClaimTimer = () => {
  if (claimTimerId) {
    clearTimeout(claimTimerId);
    claimTimerId = null;
  }
};

const scheduleNextClaim = () => {
  stopResourceClaimTimer();
  const remaining = Number(gameStore.msUntilNextTick) || 0;
  const cooldownLeft = Math.max(0, (gameStore.claimCooldownUntil || 0) - Date.now());
  const wait = Math.max(1000, remaining, cooldownLeft);

  claimTimerId = setTimeout(async () => {
    try {
      await gameStore.claimResources();
    } catch {
      /* next schedule still runs */
    }
    scheduleNextClaim();
  }, wait);
};

const onVisibilityChange = () => {
  if (document.visibilityState === "visible") {
    gameStore.claimResources().catch(() => {});
    scheduleNextClaim();
  }
};

const onWindowFocus = () => {
  gameStore.claimResources().catch(() => {});
  scheduleNextClaim();
};
</script>

<style scoped>
.gamelayout-wrapper {
  width: 100%;
  height: 100vh; /* Fill the whole viewport */
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.game-layout {
  display: flex;
  flex-direction: column;
  flex-grow: 1; /* Grow to fill the wrapper */
  box-sizing: border-box;

  /* Apply padding for the fixed header/footer HERE */
  padding-top: 70px;
  padding-bottom: 80px;
}

.game-content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-image: url("https://images.pexels.com/photos/176851/pexels-photo-176851.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2");
  background-size: cover;
  background-position: center;
  background-attachment: fixed; /* This will work here */
}

/* Floating Audio Control */
.ambiance-control {
  position: fixed;
  bottom: 100px; /* Above footer */
  right: 30px;
  z-index: 900; /* Below modals (1000+) but above content */
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: right 0.3s ease;
}

.ambiance-control.panel-active {
  right: 380px; /* Shifts right next to the 350px panel + margins */
}

/* Invisible bridge so the pointer can move from the button onto the slider */
.ambiance-control::before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 100%;
  height: 0;
  pointer-events: none;
}

.ambiance-control.volume-open::before {
  height: 96px;
  pointer-events: auto;
}

.ambiance-volume-slider {
  position: absolute;
  left: 50%;
  bottom: calc(100% + 8px);
  width: 18px;
  height: 80px;
  margin: 0;
  writing-mode: vertical-lr;
  direction: rtl;
  cursor: pointer;
  accent-color: #00f6ff;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  z-index: 1;
  transform: translateX(-50%) translateY(6px);
  transition: opacity 0.18s ease, visibility 0.18s ease, transform 0.18s ease;
}

.ambiance-control.volume-open .ambiance-volume-slider {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  transform: translateX(-50%) translateY(0);
}

.floating-audio-btn {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: rgba(36, 40, 59, 0.95);
  border: 2px solid #00f6ff;
  color: #00f6ff;
  font-size: 1.5rem;
  cursor: pointer;
  box-shadow: 0 0 20px rgba(0, 246, 255, 0.6);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.floating-audio-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 0 30px rgba(0, 246, 255, 0.9);
  background-color: rgba(0, 246, 255, 0.1);
}

.floating-audio-btn:active {
  transform: scale(0.95);
}

@media (max-width: 768px) {
  .game-layout {
    padding-top: 56px;
  }

  .ambiance-control {
    display: none !important;
  }
}
</style>
